import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, FileHeart, PackageOpen, Pill, Stethoscope, Syringe } from "lucide-react";

import { medicalService } from "../services/medicalService";
import { usePermissions } from "../hooks/usePermissions";
import { todayIso } from "../utils/dates";
import { ImmunisationsPanel } from "../components/medical/ImmunisationsPanel";
import { MedicinesPanel } from "../components/medical/MedicinesPanel";
import { PrescriptionsPanel } from "../components/medical/PrescriptionsPanel";
import { RecordsPanel } from "../components/medical/RecordsPanel";
import { VisitsPanel } from "../components/medical/VisitsPanel";
import { doseDueSoon, isOpenVisit, medicineStatus } from "../components/medical/medicalLogic";

type Tab = "visits" | "records" | "prescriptions" | "medicines" | "immunisations";
const TABS: { id: Tab; label: string; icon: typeof Stethoscope }[] = [
  { id: "visits", label: "Visits", icon: Stethoscope },
  { id: "records", label: "Health records", icon: FileHeart },
  { id: "prescriptions", label: "Prescriptions", icon: Pill },
  { id: "medicines", label: "Medicine store", icon: PackageOpen },
  { id: "immunisations", label: "Immunisations", icon: Syringe },
];
const KEYS = ["medical-records", "medical-visits", "medical-medications", "medical-prescriptions", "medical-immunizations", "medical-stock-history"];

/** The sick bay: today's visits, learners' health records, prescriptions, the medicine store and immunisations. */
export default function Medical() {
  const { hasCapability } = usePermissions();
  // Viewing and writing clinical records are separate capabilities, so an
  // admin can let someone read the sick bay without treating anyone.
  const canManage = hasCapability("access.medical.manage_records");
  const canPickClinician = canManage && hasCapability("access.medical.manage_staff");
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.some((item) => item.id === params.get("tab")) ? (params.get("tab") as Tab) : "visits";
  const view = params.get("view") ?? undefined;
  const select = (next: Tab) => setParams(next === "visits" ? {} : { tab: next }, { replace: true });

  const queryClient = useQueryClient();
  const records = useQuery({ queryKey: ["medical-records"], queryFn: medicalService.getRecords });
  const visits = useQuery({ queryKey: ["medical-visits"], queryFn: medicalService.getVisits });
  const medicines = useQuery({ queryKey: ["medical-medications"], queryFn: medicalService.getMedications });
  const prescriptions = useQuery({ queryKey: ["medical-prescriptions"], queryFn: medicalService.getPrescriptions });
  const immunisations = useQuery({ queryKey: ["medical-immunizations"], queryFn: medicalService.getImmunizations });
  const learners = useQuery({ queryKey: ["medical-learners"], queryFn: medicalService.getLearners, staleTime: 5 * 60_000 });
  const clinicians = useQuery({ queryKey: ["medical-staff"], queryFn: medicalService.getClinicians, enabled: canPickClinician, staleTime: 5 * 60_000 });
  const refresh = () => Promise.all(KEYS.map((key) => queryClient.invalidateQueries({ queryKey: [key] })));

  const today = todayIso();
  const visitList = visits.data ?? [];
  const waiting = visitList.filter((row) => row.visit_date <= today && isOpenVisit(row)).length;
  const seenToday = visitList.filter((row) => row.visit_date === today && row.status === "completed").length;
  const toDispense = (prescriptions.data ?? []).filter((row) => row.status === "prescribed").length;
  const onCourse = (prescriptions.data ?? []).filter((row) => row.status === "dispensed").length;
  const reorder = (medicines.data ?? []).filter((row) => medicineStatus(row, today).alert);
  const expired = reorder.filter((row) => row.expiry_date && row.expiry_date < today).length;
  const doses = (immunisations.data ?? []).filter((row) => doseDueSoon(row, today));
  const overdue = doses.filter((row) => row.next_dose_due! < today).length;
  const dash = (query: { isLoading: boolean }, value: number) => (query.isLoading ? "–" : String(value));
  const tiles: { label: string; value: string; note: string; tab: Tab; icon: typeof Stethoscope; alert?: boolean }[] = [
    { label: "Waiting or with the clinician", value: dash(visits, waiting), note: `${seenToday} seen today`, tab: "visits", icon: Stethoscope, alert: waiting > 0 },
    { label: "To dispense", value: dash(prescriptions, toDispense), note: `${onCourse} ${onCourse === 1 ? "learner" : "learners"} on a course`, tab: "prescriptions", icon: Pill, alert: toDispense > 0 },
    { label: "Medicines to see to", value: dash(medicines, reorder.length), note: expired ? `${expired} expired` : reorder.length ? "Low, out or expiring" : "Stock is fine", tab: "medicines", icon: PackageOpen, alert: reorder.length > 0 },
    { label: "Doses due in 30 days", value: dash(immunisations, doses.length), note: overdue ? `${overdue} overdue` : "None overdue", tab: "immunisations", icon: CalendarClock, alert: overdue > 0 },
  ];
  const badges: Partial<Record<Tab, number>> = { visits: waiting, prescriptions: toDispense, medicines: reorder.length };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sick bay</h1>
        <p className="mt-1 text-sm text-gray-500">
          {canManage
            ? "See learners who come in, keep their health records, prescribe and dispense, run the medicine store and record immunisations. Everything here is confidential."
            : "Learners seen at the sick bay, their health records, medicines and immunisations. Everything here is confidential."}
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={() => select(tile.tab)}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block text-xl font-semibold tabular-nums ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Sick bay sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {TABS.map((item) => {
          const active = tab === item.id;
          const badge = badges[item.id];
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => select(item.id)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${
                active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{badge}</span> : null}
            </button>
          );
        })}
      </nav>

      {tab === "visits" && (
        <VisitsPanel
          visits={visits.data}
          records={records.data ?? []}
          learners={learners.data ?? []}
          clinicians={clinicians.data ?? []}
          medicines={medicines.data ?? []}
          prescriptions={prescriptions.data ?? []}
          isLoading={visits.isLoading}
          isError={visits.isError}
          onRetry={() => visits.refetch()}
          canManage={canManage}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "records" && (
        <RecordsPanel
          records={records.data}
          learners={learners.data ?? []}
          visits={visitList}
          immunisations={immunisations.data ?? []}
          isLoading={records.isLoading}
          isError={records.isError}
          onRetry={() => records.refetch()}
          canManage={canManage}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "prescriptions" && (
        <PrescriptionsPanel
          prescriptions={prescriptions.data}
          visits={visitList}
          records={records.data ?? []}
          medicines={medicines.data ?? []}
          isLoading={prescriptions.isLoading}
          isError={prescriptions.isError}
          onRetry={() => prescriptions.refetch()}
          canManage={canManage}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "medicines" && (
        <MedicinesPanel
          medicines={medicines.data}
          isLoading={medicines.isLoading}
          isError={medicines.isError}
          onRetry={() => medicines.refetch()}
          canManage={canManage}
          onChanged={refresh}
          initialView={view}
        />
      )}
      {tab === "immunisations" && (
        <ImmunisationsPanel
          immunisations={immunisations.data}
          learners={learners.data ?? []}
          records={records.data ?? []}
          isLoading={immunisations.isLoading}
          isError={immunisations.isError}
          onRetry={() => immunisations.refetch()}
          canManage={canManage}
          onChanged={refresh}
          initialView={view}
        />
      )}
    </div>
  );
}
